import { useState } from 'react';
import { useReactFlow } from '@xyflow/react';
import { useEditorStore } from '../state/editor-store';
import { readPresentationExtension, toPresentationGraphInput } from './graph-bridge';
import { normalizePresentationConfig } from '@/domain/presentation/normalize';
import {
  PRESENTATION_BRANDING_MODES,
  PRESENTATION_THEMES,
  type PresentationBrandingMode,
  type PresentationTheme,
} from '@/domain/presentation/contracts';
import {
  uploadPresentationLogo,
  presentationLogoUrl,
} from '@/features/presentation/api/presentation-asset-api';

/**
 * SPEC-011 §66/§67/plan §34 — the editor's "Presentation settings" drawer.
 * Config-only: mutations here go through the same `commit()` pipeline as
 * every other editor action (autosave/undo/redo, §64/§73/§74). Kept
 * focused (plan §34: "do not create a complex CMS") — one field/entity at
 * a time, matching SPEC-009's SimulationPanel scope decision.
 */
export function PresentationSettingsPanel({
  funnelId,
  onClose,
}: {
  funnelId: string;
  onClose: () => void;
}) {
  const graph = useEditorStore((state) => state.graph);
  const updatePresentationMeta = useEditorStore((state) => state.updatePresentationMeta);
  const setPresentationTheme = useEditorStore((state) => state.setPresentationTheme);
  const setPresentationBranding = useEditorStore((state) => state.setPresentationBranding);
  const setPresentationLogoAsset = useEditorStore((state) => state.setPresentationLogoAsset);
  const setPresentationDefaultViewport = useEditorStore(
    (state) => state.setPresentationDefaultViewport,
  );
  const createStrategicNote = useEditorStore((state) => state.createStrategicNote);
  const updateStrategicNote = useEditorStore((state) => state.updateStrategicNote);
  const deleteStrategicNote = useEditorStore((state) => state.deleteStrategicNote);
  const createHighlight = useEditorStore((state) => state.createHighlight);
  const updateHighlight = useEditorStore((state) => state.updateHighlight);
  const deleteHighlight = useEditorStore((state) => state.deleteHighlight);
  const { getViewport } = useReactFlow();

  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newHighlightLabel, setNewHighlightLabel] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  if (!graph) return null;
  const extension = readPresentationExtension(graph);
  const graphInput = toPresentationGraphInput(graph);
  const { warnings } = normalizePresentationConfig(extension, graphInput);
  const nodeOptions = graph.nodes.map((node) => ({
    id: node.id,
    name: (node.data as { name?: string })?.name ?? node.id,
  }));
  const edgeOptions = graph.edges.map((edge) => ({ id: edge.id, label: edge.id }));

  function toggleInArray(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  async function handleLogoUpload(file: File) {
    setUploadingLogo(true);
    setLogoError(null);
    try {
      const asset = await uploadPresentationLogo(funnelId, file);
      setPresentationLogoAsset(asset.id);
    } catch (error) {
      setLogoError(error instanceof Error ? error.message : 'Falha ao enviar o logotipo.');
    } finally {
      setUploadingLogo(false);
    }
  }

  function handleLogoRemove() {
    if (!extension.companyLogoAssetId) return;
    // The reference participates in graph undo/redo. Keep the private asset
    // intact so undo can safely restore it (SPEC-011 §76/AC-074).
    setPresentationLogoAsset(undefined);
  }

  return (
    <aside
      aria-label="Configurações da apresentação"
      className="flex h-full w-[420px] flex-col overflow-y-auto border-l border-border-subtle bg-surface p-4 text-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-primary">Configurações da apresentação</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-text-muted hover:text-text-primary"
        >
          Fechar
        </button>
      </div>

      {warnings.length > 0 ? (
        <div
          role="alert"
          className="mb-3 rounded border border-[rgba(226,85,85,0.35)] bg-[rgba(226,85,85,0.1)] p-2 text-xs text-[#f08484]"
        >
          {warnings.map((w) => (
            <p key={`${w.code}-${w.entityId}`}>{w.message}</p>
          ))}
        </div>
      ) : null}

      <section className="mb-4 flex flex-col gap-2">
        <h3 className="text-[10px] uppercase tracking-wide text-text-muted">Geral</h3>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Título
          <input
            className="rounded border border-border-subtle bg-bg-app px-2 py-1"
            value={extension.title ?? ''}
            maxLength={160}
            onChange={(e) => updatePresentationMeta({ title: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Subtítulo
          <input
            className="rounded border border-border-subtle bg-bg-app px-2 py-1"
            value={extension.subtitle ?? ''}
            maxLength={300}
            onChange={(e) => updatePresentationMeta({ subtitle: e.target.value })}
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-text-secondary">
          <input
            type="checkbox"
            checked={extension.coverEnabled}
            onChange={(e) => updatePresentationMeta({ coverEnabled: e.target.checked })}
          />
          Mostrar tela de abertura
        </label>
        <label className="flex items-center gap-2 text-xs text-text-secondary">
          <input
            type="checkbox"
            checked={extension.showMetrics}
            onChange={(e) => updatePresentationMeta({ showMetrics: e.target.checked })}
          />
          Mostrar métricas ao clicar
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-secondary">
          Tema
          <select
            aria-label="Tema da apresentação"
            className="rounded border border-border-subtle bg-bg-app px-2 py-1"
            value={extension.theme}
            onChange={(e) => setPresentationTheme(e.target.value as PresentationTheme)}
          >
            {PRESENTATION_THEMES.map((theme) => (
              <option key={theme} value={theme}>
                {theme}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="self-start rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
          onClick={() => setPresentationDefaultViewport(getViewport())}
        >
          Usar a visualização atual como padrão da apresentação
        </button>
      </section>

      <section className="mb-4 flex flex-col gap-2">
        <h3 className="text-[10px] uppercase tracking-wide text-text-muted">Marca</h3>
        <select
          aria-label="Modo de marca"
          className="rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
          value={extension.brandingMode}
          onChange={(e) => setPresentationBranding(e.target.value as PresentationBrandingMode)}
        >
          {PRESENTATION_BRANDING_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {mode}
            </option>
          ))}
        </select>
        {extension.brandingMode === 'client' ? (
          <>
            <label className="flex flex-col gap-1 text-xs text-text-secondary">
              Nome da empresa
              <input
                className="rounded border border-border-subtle bg-bg-app px-2 py-1"
                value={extension.companyName ?? ''}
                maxLength={160}
                onChange={(e) => updatePresentationMeta({ companyName: e.target.value })}
              />
            </label>
            {extension.companyLogoAssetId ? (
              <div className="flex items-center gap-2">
                <img
                  src={presentationLogoUrl(funnelId, extension.companyLogoAssetId)}
                  alt="Logotipo da empresa"
                  className="h-10 max-w-[120px] object-contain"
                />
                <button type="button" onClick={handleLogoRemove} className="text-xs text-[#f08484]">
                  Remover logotipo
                </button>
              </div>
            ) : (
              <label className="text-xs text-text-secondary">
                Enviar logotipo (PNG/JPEG/WEBP, máximo de 5 MB)
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={uploadingLogo}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleLogoUpload(file);
                  }}
                  className="mt-1 block text-xs"
                />
              </label>
            )}
            {logoError ? <p className="text-xs text-[#f08484]">{logoError}</p> : null}
          </>
        ) : null}
      </section>

      <section className="mb-4 flex flex-col gap-2">
        <h3 className="text-[10px] uppercase tracking-wide text-text-muted">Observações estratégicas</h3>
        {extension.strategicNotes.map((note) => (
          <div key={note.id} className="rounded border border-border-subtle p-2">
            <input
              className="mb-1 w-full rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
              value={note.title}
              maxLength={120}
              onChange={(e) => updateStrategicNote(note.id, { title: e.target.value })}
            />
            <textarea
              className="mb-1 w-full rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
              value={note.body}
              maxLength={4000}
              rows={2}
              onChange={(e) => updateStrategicNote(note.id, { body: e.target.value })}
            />
            <label className="mb-1 block text-[10px] text-text-muted">Nós vinculados</label>
            <select
              multiple
              aria-label={`Nós da observação ${note.title}`}
              className="mb-1 w-full rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
              value={note.nodeIds}
              onChange={(e) =>
                updateStrategicNote(note.id, {
                  nodeIds: toggleInArray(note.nodeIds, e.target.value),
                })
              }
            >
              {nodeOptions.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.name}
                </option>
              ))}
            </select>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1 text-xs text-text-secondary">
                <input
                  type="checkbox"
                  checked={note.visible}
                  onChange={(e) => updateStrategicNote(note.id, { visible: e.target.checked })}
                />
                Visível
              </label>
              <button
                type="button"
                onClick={() => deleteStrategicNote(note.id)}
                className="text-xs text-[#f08484]"
              >
                Excluir
              </button>
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            aria-label="Título da nova observação"
            placeholder="Título da nova observação"
            className="flex-1 rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
            value={newNoteTitle}
            onChange={(e) => setNewNoteTitle(e.target.value)}
          />
          <button
            type="button"
            disabled={!newNoteTitle.trim()}
            onClick={() => {
              createStrategicNote({ title: newNoteTitle.trim() });
              setNewNoteTitle('');
            }}
            className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary disabled:opacity-40"
          >
            Adicionar observação
          </button>
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-[10px] uppercase tracking-wide text-text-muted">Destaques</h3>
        {extension.highlights.map((highlight) => (
          <div key={highlight.id} className="rounded border border-border-subtle p-2">
            <input
              className="mb-1 w-full rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
              value={highlight.label}
              maxLength={120}
              onChange={(e) => updateHighlight(highlight.id, { label: e.target.value })}
            />
            <label className="mb-1 block text-[10px] text-text-muted">Nós</label>
            <select
              multiple
              aria-label={`Nós de ${highlight.label}`}
              className="mb-1 w-full rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
              value={highlight.nodeIds}
              onChange={(e) => {
                const id = e.target.value;
                updateHighlight(highlight.id, { nodeIds: toggleInArray(highlight.nodeIds, id) });
              }}
            >
              {nodeOptions.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.name}
                </option>
              ))}
            </select>
            <label className="mb-1 block text-[10px] text-text-muted">Conexões</label>
            <select
              multiple
              aria-label={`Conexões de ${highlight.label}`}
              className="mb-1 w-full rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
              value={highlight.edgeIds}
              onChange={(e) => {
                const id = e.target.value;
                updateHighlight(highlight.id, { edgeIds: toggleInArray(highlight.edgeIds, id) });
              }}
            >
              {edgeOptions.map((edge) => (
                <option key={edge.id} value={edge.id}>
                  {edge.label}
                </option>
              ))}
            </select>
            <label className="mb-1 block text-[10px] text-text-muted">Observação vinculada</label>
            <select
              aria-label={`Observação de ${highlight.label}`}
              className="mb-1 w-full rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
              value={highlight.noteId ?? ''}
              onChange={(e) =>
                updateHighlight(highlight.id, { noteId: e.target.value || undefined })
              }
            >
              <option value="">Nenhuma</option>
              {extension.strategicNotes.map((note) => (
                <option key={note.id} value={note.id}>
                  {note.title}
                </option>
              ))}
            </select>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-1 text-xs text-text-secondary">
                <input
                  type="checkbox"
                  checked={highlight.emphasis === 'strong'}
                  onChange={(e) =>
                    updateHighlight(highlight.id, {
                      emphasis: e.target.checked ? 'strong' : 'normal',
                    })
                  }
                />
                Ênfase forte
              </label>
              <button
                type="button"
                onClick={() => deleteHighlight(highlight.id)}
                className="text-xs text-[#f08484]"
              >
                Excluir
              </button>
            </div>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            aria-label="Rótulo do novo destaque"
            placeholder="Rótulo do novo destaque"
            className="flex-1 rounded border border-border-subtle bg-bg-app px-2 py-1 text-xs"
            value={newHighlightLabel}
            onChange={(e) => setNewHighlightLabel(e.target.value)}
          />
          <button
            type="button"
            disabled={!newHighlightLabel.trim()}
            onClick={() => {
              createHighlight({ label: newHighlightLabel.trim() });
              setNewHighlightLabel('');
            }}
            className="rounded border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary disabled:opacity-40"
          >
            Adicionar destaque
          </button>
        </div>
      </section>
    </aside>
  );
}
